"use client";

import { Button } from "@spear-ai/ui/components/button";
import { Dialog, DialogModal, DialogModalOverlay, DialogTitle } from "@spear-ai/ui/components/dialog";
import {
  Select,
  SelectButton,
  SelectFieldError,
  SelectIcon,
  SelectLabel,
  SelectListBox,
  SelectListBoxItem,
  SelectListBoxItemCheck,
  SelectListBoxItemCheckIcon,
  SelectListBoxItemLabel,
  SelectPopover,
  SelectValue,
} from "@spear-ai/ui/components/select";
import {
  TextField,
  TextFieldError,
  TextFieldInput,
  TextFieldLabel,
  TextFieldTextArea,
} from "@spear-ai/ui/components/text-field";
import { useState } from "react";
import { Key } from "react-aria-components";
import { Controller, useForm } from "react-hook-form";
import { useIntl } from "react-intl";

type FeedbackFormData = {
  description: string;
  email: string;
  feedbackType: string;
  name: string;
};

const feedbackTypes = [
  { id: "bug-report", label: "Bug Report" },
  { id: "data-correction", label: "Item Description or Data Correction" },
  { id: "new-item", label: "New Item to Include" },
  { id: "ui-suggestions", label: "User Interface Suggestions" },
  { id: "other", label: "Other" },
];

export const FeedbackDialog = ({
  isOpen,
  onOpenChange,
  userEmail,
  userName,
}: {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  userEmail?: string | undefined;
  userName?: string | undefined;
}) => {
  const intl = useIntl();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { control, handleSubmit, reset } = useForm<FeedbackFormData>({
    defaultValues: {
      description: "",
      email: userEmail ?? "",
      feedbackType: "",
      name: userName ?? "",
    },
  });

  const onSubmit = async (data: FeedbackFormData) => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const response = await fetch("/api/feedback", {
        body: JSON.stringify(data),
        headers: {
          "Content-Type": "application/json",
        },
        method: "POST",
      });

      if (!response.ok) {
        const errorData = (await response.json()) as { message?: string };
        const errorMessage = errorData.message;
        throw new Error(errorMessage ?? "Failed to submit feedback");
      }

      setIsSubmitted(true);

      // Close dialog after showing success state briefly
      setTimeout(() => {
        setIsSubmitted(false);
        setSubmitError(null);
        reset({
          description: "",
          email: userEmail ?? "",
          feedbackType: "",
          name: userName ?? "",
        });
        onOpenChange(false);
      }, 1500);
    } catch (error) {
      setSubmitError(
        error instanceof Error ? error.message : "An unexpected error occurred. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setIsSubmitted(false);
      setSubmitError(null);
      reset({
        description: "",
        email: userEmail ?? "",
        feedbackType: "",
        name: userName ?? "",
      });
      onOpenChange(false);
    }
  };

  return (
    <DialogModalOverlay isDismissable={!isSubmitting} isOpen={isOpen} onOpenChange={handleClose}>
      <DialogModal className="mx-4 my-8 w-full max-w-lg sm:mx-auto">
        <Dialog>
          <div className="flex max-h-screen flex-col">
            <div className="shrink-0 p-6 pb-4">
              <DialogTitle className="text-lg font-semibold text-neutral-12">
                {intl.formatMessage({
                  defaultMessage: "Provide Feedback",
                  id: "zclava",
                })}
              </DialogTitle>
            </div>

            <div className="flex-1 overflow-y-auto px-6">
              {/* Show error message if submission failed */}
              {submitError != null && submitError.length > 0 && (
                <div className="mb-4 rounded-md border border-x-negative-6 bg-x-negative-2 p-3 text-x-negative-11">
                  {submitError}
                </div>
              )}

              {/* Show success message */}
              {isSubmitted && (
                <div className="mb-4 rounded-md border border-primary-6 bg-primary-2 p-3 text-primary-11">
                  {intl.formatMessage({
                    defaultMessage: "Thank you! Your feedback has been submitted successfully.",
                    id: "HB2kQc",
                  })}
                </div>
              )}

              {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
              <form className="flex flex-col gap-4" id="feedback-form" onSubmit={handleSubmit(onSubmit)}>
                <Controller
                  control={control}
                  name="name"
                  render={({
                    field: { name, onBlur, onChange, ref, value },
                    fieldState: { error, invalid },
                  }) => (
                    <TextField
                      isInvalid={invalid}
                      name={name}
                      onBlur={onBlur}
                      onChange={onChange}
                      type="text"
                      validationBehavior="aria"
                      value={value}
                    >
                      <TextFieldLabel>
                        {intl.formatMessage({
                          defaultMessage: "Name",
                          id: "HAlOn1",
                        })}
                      </TextFieldLabel>
                      <TextFieldInput ref={ref} type="text" />
                      <TextFieldError>{error?.message}</TextFieldError>
                    </TextField>
                  )}
                />

                <Controller
                  control={control}
                  name="email"
                  render={({
                    field: { name, onBlur, onChange, ref, value },
                    fieldState: { error, invalid },
                  }) => (
                    <TextField
                      isInvalid={invalid}
                      isRequired
                      name={name}
                      onBlur={onBlur}
                      onChange={onChange}
                      type="email"
                      validationBehavior="aria"
                      value={value}
                    >
                      <TextFieldLabel>
                        {intl.formatMessage({
                          defaultMessage: "Email Address",
                          id: "xxQxLE",
                        })}
                      </TextFieldLabel>
                      <TextFieldInput ref={ref} type="email" />
                      <TextFieldError>{error?.message}</TextFieldError>
                    </TextField>
                  )}
                  rules={{
                    pattern: {
                      message: "Invalid email address",
                      value: /^[\w%+\-.]+@[\d\-.a-z]+\.[a-z]{2,}$/iu,
                    },
                    required: "Email is required",
                  }}
                />

                <Controller
                  control={control}
                  name="feedbackType"
                  render={({ field: { onChange, value }, fieldState: { error, invalid } }) => (
                    <Select
                      isInvalid={invalid}
                      isRequired
                      onSelectionChange={(key: Key | null) => {
                        onChange(key as string);
                      }}
                      selectedKey={value}
                      validationBehavior="aria"
                    >
                      <SelectLabel>
                        {intl.formatMessage({
                          defaultMessage: "Feedback Type",
                          id: "A2Jjyd",
                        })}
                      </SelectLabel>
                      <SelectButton>
                        <SelectValue />
                        <SelectIcon />
                      </SelectButton>
                      <SelectPopover>
                        <SelectListBox>
                          {feedbackTypes.map((type) => (
                            <SelectListBoxItem id={type.id} key={type.id} textValue={type.label}>
                              <SelectListBoxItemCheck>
                                <SelectListBoxItemCheckIcon />
                              </SelectListBoxItemCheck>
                              <SelectListBoxItemLabel>{type.label}</SelectListBoxItemLabel>
                            </SelectListBoxItem>
                          ))}
                        </SelectListBox>
                      </SelectPopover>
                      <SelectFieldError>{error?.message}</SelectFieldError>
                    </Select>
                  )}
                  rules={{ required: "Please select a feedback type" }}
                />

                <Controller
                  control={control}
                  name="description"
                  render={({
                    field: { name, onBlur, onChange, ref, value },
                    fieldState: { error, invalid },
                  }) => (
                    <TextField
                      isInvalid={invalid}
                      isRequired
                      name={name}
                      onBlur={onBlur}
                      onChange={onChange}
                      validationBehavior="aria"
                      value={value}
                    >
                      <TextFieldLabel>
                        {intl.formatMessage({
                          defaultMessage: "Description",
                          id: "Q8Qw5B",
                        })}
                      </TextFieldLabel>
                      <TextFieldTextArea ref={ref} rows={4} />
                      <TextFieldError>{error?.message}</TextFieldError>
                    </TextField>
                  )}
                  rules={{ required: "Description is required" }}
                />
              </form>
            </div>

            <div className="shrink-0 p-6 pt-4">
              <div className="flex justify-end gap-3">
                <Button
                  className="inline-flex justify-center rounded-md px-3 py-2 text-sm font-semibold text-neutral-11 shadow-sm ring-1 ring-inset ring-neutral-a-7 active:bg-neutral-a-5 hover:bg-neutral-a-4"
                  onPress={handleClose}
                  variant="outline"
                >
                  {intl.formatMessage({
                    defaultMessage: "Cancel",
                    id: "47FYwb",
                  })}
                </Button>
                <Button
                  className="rounded-md bg-primary-9 px-3 py-2 text-sm font-semibold text-primary-contrast shadow-sm hover:bg-primary-10 hover:outline-primary-8 disabled:cursor-not-allowed disabled:opacity-75"
                  color="primary"
                  form="feedback-form"
                  isDisabled={isSubmitting || isSubmitted}
                  type="submit"
                  variant="solid"
                >
                  {isSubmitting
                    ? intl.formatMessage({
                        defaultMessage: "Submitting...",
                        id: "txkW56",
                      })
                    : intl.formatMessage({
                        defaultMessage: "Submit Feedback",
                        id: "+ASm/B",
                      })}
                </Button>
              </div>
            </div>
          </div>
        </Dialog>
      </DialogModal>
    </DialogModalOverlay>
  );
};
